/**
 * Page heading + 50-50 map viewer | phase card, sized to fit — never scrolls.
 * `minHeight: 0` on the content row and scrolling panel is load-bearing: without
 * it a tall phase card refuses to shrink and pushes the page past the fold.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BoltIcon from "@mui/icons-material/Bolt";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PropTypes from "prop-types";

import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import LightController from "./LightController";
import LiveRobotPositionViewer from "../../utils/LiveRobotPositionViewer";
import { MapSelectionDialogModal } from "./MapSelectionDialog";
import { Icon } from "../ui";
import { tokens } from "../../theme";

/** Breadcrumb trail for a robot-operations page: Robots ▸ (robot ▸) `title`.
 *  The robot segment appears only when the flow was opened from Robot Details. */
export const buildOperationCrumbs = (title, fromRobotId, robot) => [
  { label: "Robots", route: "/robots" },
  ...(fromRobotId
    ? [{ label: robot?.name || "Robot", route: `/robots/${fromRobotId}?tab=operations` }]
    : []),
  { label: title },
];

/** Page heading: back button + icon + title (+ breadcrumb trail) on the left,
 *  `actions` right-aligned. The back button targets the parent crumb unless
 *  `onBack` overrides it. A trail of fewer than 2 crumbs renders nothing. */
export const PageHead = ({ title, icon, crumbs, actions, onBack, sx }) => {
  const navigate = useNavigate();
  const parent = crumbs?.length > 1 ? crumbs.at(-2) : null;
  const handleBack = onBack || (parent?.route ? () => navigate(parent.route) : null);
  const backLabel = parent ? `Back to ${parent.label}` : "Back";
  return (
    <Box sx={{ mb: 2, minWidth: 0, ...sx }}>
      {crumbs?.length > 1 && (
        <Stack
          component="nav"
          aria-label="Breadcrumb"
          direction="row"
          alignItems="center"
          gap={0.5}
          flexWrap="wrap"
          sx={{ mb: 1.25, minWidth: 0 }}
        >
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <Stack key={crumb.route || crumb.label} direction="row" alignItems="center" gap={0.5}>
                {crumb.route && !isLast ? (
                  <Box
                    component="button"
                    onClick={() => navigate(crumb.route)}
                    sx={{
                      font: "inherit",
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: tokens.text2,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      p: 0,
                      "&:hover": { color: tokens.accent },
                    }}
                  >
                    {crumb.label}
                  </Box>
                ) : (
                  <Typography
                    component="span"
                    aria-current={isLast ? "page" : undefined}
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: isLast ? tokens.text : tokens.text2,
                    }}
                  >
                    {crumb.label}
                  </Typography>
                )}
                {!isLast && <Icon name="chevron-right" size={13} style={{ color: tokens.text3 }} />}
              </Stack>
            );
          })}
        </Stack>
      )}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
          {handleBack && (
            <IconButton
              onClick={handleBack}
              aria-label={backLabel}
              title={backLabel}
              sx={{
                width: 36,
                height: 36,
                flex: "none",
                borderRadius: "10px",
                color: tokens.text2,
                border: `1px solid ${tokens.border}`,
                background: tokens.surface,
                "&:hover": { color: tokens.text, background: tokens.surface2 },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                flex: "none",
                display: "grid",
                placeItems: "center",
                background: tokens.accentSoft,
                color: tokens.accent,
              }}
            >
              <Icon name={icon} size={20} />
            </Box>
          )}
          <Typography
            component="h1"
            sx={{
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "-.02em",
              color: tokens.text,
              lineHeight: 1.2,
              m: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>
        {actions && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

PageHead.propTypes = {
  title: PropTypes.node.isRequired,
  icon: PropTypes.string,
  crumbs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, route: PropTypes.string })
  ),
  actions: PropTypes.node,
  /** Overrides the default "go to parent crumb" back action. */
  onBack: PropTypes.func,
  sx: PropTypes.object,
};

const DeliveryLayout = ({
  children,
  rightPanel,
  selectedMap,
  navigationTarget,
  previewLocations,
  robot,
  fromRobotId,
  batteryLevel,
  batteryCharging,
  showMapSelectionModalDialog,
  closeMapSelectionDialogModal,
  portalRobots,
  onRobotChange,
  onEmergencyResume,
  isRobotConnected,
  navProgress,
  isStopped,
  // Defaulted rather than required: every existing caller (RoomNumberPageAero
  // and its phase files) predates these props and relies on the Dispatch
  // Delivery defaults, so they must keep rendering identically unset.
  title = "Dispatch Delivery",
  icon = "send",
}) => {
  const [lightOpen, setLightOpen] = useState(false);
  const [emergencyActive, setEmergencyActive] = useState(false);
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);

  const handleEmergencyStop = async () => {
    if (!robot?.serialID) return;
    try {
      const token = localStorage.getItem("portalToken");
      await apiFetch(`${CONFIG.BASE_URL}/api/adbToSocket/emergency-stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ serialID: robot.serialID }),
      });
      setEmergencyActive(true);
      setEmergencyModalOpen(true);
      window.dispatchEvent(new CustomEvent("delivery:emergencyStop"));
    } catch (err) {
      console.error("[EmergencyStop] error:", err);
    }
  };

  const handleEmergencyResume = async () => {
    if (!robot?.serialID) return;
    try {
      const token = localStorage.getItem("portalToken");
      await apiFetch(`${CONFIG.BASE_URL}/api/adbToSocket/emergency-resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ serialID: robot.serialID }),
      });
      setEmergencyActive(false);
      setEmergencyModalOpen(false);
      window.dispatchEvent(new CustomEvent("delivery:emergencyResume"));
      onEmergencyResume?.();
    } catch (err) {
      console.error("[EmergencyResume] error:", err);
    }
  };

  const crumbs = buildOperationCrumbs(title, fromRobotId, robot);

  // Robot picker + battery/connection chips + Light + E-Stop, in PageHead's
  // `actions` slot so the page has one header row, not a separate toolbar.
  // Grouped into three visually distinct cards — robot/status, Light, E-Stop —
  // so each control reads as its own unit rather than a loose row of chips.
  const headerActions = (
    <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
      {/* Robot identity + connection + battery, grouped in one pill card */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        sx={{
          height: 40,
          pl: 1.25,
          pr: 1.5,
          borderRadius: "12px",
          border: `1px solid ${tokens.border}`,
          background: tokens.surface,
        }}
      >
        {portalRobots?.length > 0 ? (
          <Select
            size="small"
            value={robot?._id || ""}
            onChange={(e) => onRobotChange?.(e.target.value)}
            displayEmpty
            renderValue={(val) => (
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "7px",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    background: tokens.accentSoft,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 14, color: tokens.accent }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: tokens.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {portalRobots.find((r) => r._id === val)?.name || "— Select Robot —"}
                </Typography>
              </Stack>
            )}
            sx={{
              minWidth: 120,
              maxWidth: 170,
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiSelect-select": {
                py: "4px !important",
                pl: "2px !important",
                pr: "24px !important",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: 13, color: tokens.text3 }}>
              — Select Robot —
            </MenuItem>
            {portalRobots.map((r) => (
              <MenuItem key={r._id} value={r._id} sx={{ fontSize: 13 }}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Stack direction="row" alignItems="center" gap={0.75}>
            <SmartToyIcon sx={{ fontSize: 16, color: tokens.text3 }} />
          </Stack>
        )}

        {isRobotConnected !== undefined && (
          <>
            <Box sx={{ width: "1px", height: 18, background: tokens.border, flexShrink: 0 }} />
            <Stack direction="row" alignItems="center" gap={0.6} sx={{ flexShrink: 0 }}>
              <FiberManualRecordIcon
                sx={{
                  fontSize: 9,
                  color: isRobotConnected !== false ? tokens.success : tokens.danger,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: isRobotConnected !== false ? tokens.success : tokens.danger,
                }}
              >
                {isRobotConnected !== false ? "Connected" : "Disconnected"}
              </Typography>
            </Stack>
          </>
        )}

        {typeof batteryLevel === "number" && (
          <>
            <Box sx={{ width: "1px", height: 18, background: tokens.border, flexShrink: 0 }} />
            <Stack direction="row" alignItems="center" gap={0.6} sx={{ flexShrink: 0 }}>
              {batteryCharging ? (
                <BoltIcon sx={{ fontSize: 15, color: tokens.warning }} />
              ) : (
                <BatteryFullIcon sx={{ fontSize: 15, color: tokens.text2 }} />
              )}
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: batteryCharging ? tokens.warning : tokens.text2,
                  whiteSpace: "nowrap",
                }}
              >
                Battery {Math.round(batteryLevel * 100) / 100}%
              </Typography>
            </Stack>
          </>
        )}
      </Stack>

      {/* Light control — its own neutral pill button */}
      <Button
        onClick={() => setLightOpen(true)}
        startIcon={<LightbulbIcon sx={{ fontSize: "16px !important" }} />}
        sx={{
          height: 40,
          px: 2,
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 700,
          fontSize: 13,
          color: tokens.text,
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          boxShadow: "none",
          "&:hover": { background: tokens.surface2, boxShadow: "none" },
        }}
      >
        Light
      </Button>

      {/* Emergency Stop — bold red card with a caution caption underneath,
          so its weight in the header matches how destructive the action is. */}
      <Box>
        {emergencyActive ? (
          <Button
            onClick={handleEmergencyResume}
            startIcon={<PlayArrowIcon sx={{ fontSize: "17px !important" }} />}
            sx={{
              height: 40,
              px: 2.25,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13.5,
              color: "#fff",
              background: tokens.success,
              boxShadow: "none",
              "&:hover": { background: tokens.success, boxShadow: "none" },
            }}
          >
            Resume
          </Button>
        ) : (
          <Button
            onClick={handleEmergencyStop}
            startIcon={<WarningAmberIcon sx={{ fontSize: "17px !important" }} />}
            disabled={!robot?.serialID}
            sx={{
              height: 40,
              px: 2.25,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13.5,
              color: "#fff",
              background: tokens.danger,
              boxShadow: "none",
              "&:hover": { background: tokens.danger, boxShadow: "none" },
              "&:disabled": { color: "#fff", opacity: 0.4 },
            }}
          >
            Emergency Stop
          </Button>
        )}
        <Typography
          sx={{
            mt: 0.4,
            fontSize: 10.5,
            fontWeight: 600,
            color: tokens.text3,
            textAlign: "center",
          }}
        >
          Use only if required
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box
      sx={{
        // 100% of MainLayout's scroll container, not 100dvh: `dvh` ignores the
        // fixed Header's height, overflowing by exactly that and double-scrolling.
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        color: tokens.text,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* ─── Page heading ─── */}
      <Box sx={{ flex: "none", px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 } }}>
        <PageHead
          title={title}
          icon={icon}
          crumbs={crumbs}
          actions={headerActions}
          sx={{ mb: { xs: 1.5, sm: 2 } }}
        />
      </Box>

      {/* ─── Main content: map | phase panel, filling the remaining height ─── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2.5, md: 3 },
          alignItems: "stretch",
          overflowY: { xs: "auto", md: "hidden" },
        }}
      >
        {/* Left: map viewer — fills the content row's height */}
        <Box
          sx={{
            flex: 1.15,
            minWidth: 0,
            minHeight: { xs: 360, md: 0 },
            border: `1px solid ${tokens.border}`,
            borderRadius: "16px",
            overflow: "hidden",
            background: tokens.surface,
            boxShadow: tokens.shadowLg,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {robot ? (
            <LiveRobotPositionViewer
              mapId={selectedMap?._id}
              navigationTarget={navigationTarget}
              previewLocations={previewLocations}
              selectedSerialID={robot?.serialID ?? null}
              navProgress={navProgress}
              isStopped={isStopped}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <SmartToyIcon sx={{ fontSize: 52, color: tokens.text3 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.text2 }}>
                No robot selected
              </Typography>
              <Typography sx={{ fontSize: 12, color: tokens.text3 }}>
                Please select a robot to view the map
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right: phase panel — transparent; the phase's own SectionCard draws the
            surface. `& > *: flex 1` stretches it to match the map column's height. */}
        <Box
          sx={{
            flex: 1,
            minWidth: { md: 380 },
            maxWidth: { md: 460 },
            minHeight: { xs: 360, md: 0 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: { xs: "visible", md: "auto" },
            px: { md: "2px" },
            "& > *": {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {rightPanel}
          {children}
        </Box>
      </Box>

      {/* Map selection modal */}
      {showMapSelectionModalDialog && (
        <MapSelectionDialogModal
          showMapSelectionModalDialog={showMapSelectionModalDialog}
          closeMapSelectionDialogModal={closeMapSelectionDialogModal}
        />
      )}

      {/* ─── Light Drawer ─── */}
      <Drawer
        anchor="right"
        open={lightOpen}
        onClose={() => setLightOpen(false)}
        sx={{ "& .MuiDrawer-paper": { width: 380, maxWidth: "92vw" } }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${tokens.border}`,
            background: tokens.warningSoft,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <LightbulbIcon sx={{ color: tokens.warning, fontSize: 22, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: 15, color: tokens.text, lineHeight: 1.25 }}
            >
              Light Controller
            </Typography>
            <Typography sx={{ fontSize: 11, color: tokens.text2, mt: 0.2 }}>
              Control robot LED lighting effects
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setLightOpen(false)}
            sx={{
              color: tokens.text2,
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: tokens.surface,
              "&:hover": { background: tokens.surface3 },
              flexShrink: 0,
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {/* No `robot` prop — drives whichever robot is in `selectedRobo`,
              exactly as this Drawer did before the rebuild. */}
          <LightController />
        </Box>
      </Drawer>

      {/* ─── Emergency STOPPED resume modal ─── */}
      <Dialog
        open={emergencyModalOpen}
        onClose={() => setEmergencyModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: tokens.danger }}>
          <CancelIcon sx={{ color: tokens.danger }} />
          Emergency Stop
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Robot stopped. Check the robot and clear any hazard before resuming.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setEmergencyModalOpen(false)}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 13 }}
          >
            Dismiss
          </Button>
          <Button
            onClick={handleEmergencyResume}
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: 13 }}
          >
            Resume
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DeliveryLayout.propTypes = {
  children: PropTypes.node,
  rightPanel: PropTypes.node,
  selectedMap: PropTypes.object,
  navigationTarget: PropTypes.object,
  previewLocations: PropTypes.array,
  robot: PropTypes.object,
  /** Origin robot id when this flow was reached from Robot Details ▸ Operations. */
  fromRobotId: PropTypes.string,
  batteryLevel: PropTypes.number,
  batteryCharging: PropTypes.bool,
  showMapSelectionModalDialog: PropTypes.bool,
  closeMapSelectionDialogModal: PropTypes.func,
  portalRobots: PropTypes.array,
  onRobotChange: PropTypes.func,
  onEmergencyResume: PropTypes.func,
  isRobotConnected: PropTypes.bool,
  navProgress: PropTypes.number,
  isStopped: PropTypes.bool,
  title: PropTypes.string,
  icon: PropTypes.string,
};

export default DeliveryLayout;
